"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import debounce from "lodash/debounce";
import { ChevronDown } from "lucide-react";
import type { FacetBucket } from "modules/collections/details/model/collection";
import type { GameId } from "modules/common/model/models";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import { FacetDistribution } from "./FacetDistribution";
import { formatFacetLabel } from "./facetPresentation";

interface FacetHybridRangeProps {
  title: string;
  buckets: FacetBucket[];
  min: number;
  max: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  selectedSpecial: string[];
  onToggleSpecial: (value: string) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  gameExclusive?: GameId;
  onClear?: () => void;
}

function isNumericValue(value: string | number): boolean {
  if (typeof value === "number") return true;
  return value !== "" && !isNaN(Number(value));
}

export function FacetHybridRange({
  title,
  buckets,
  min,
  max,
  onMinChange,
  onMaxChange,
  selectedSpecial,
  onToggleSpecial,
  isOpen: controlledIsOpen,
  onToggleOpen,
  gameExclusive,
  onClear,
}: FacetHybridRangeProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalOpen;
  const handleToggle = onToggleOpen ?? (() => setInternalOpen((o) => !o));

  const { numericBuckets, specialBuckets, lowerBound, upperBound } =
    useMemo(() => {
      const safe = buckets ?? [];
      const numeric: FacetBucket[] = [];
      const special: FacetBucket[] = [];

      for (const b of safe) {
        if (isNumericValue(b.value)) {
          numeric.push(b);
        } else {
          special.push(b);
        }
      }

      const values = numeric
        .map((b) => Number(b.value))
        .filter(Number.isFinite);
      return {
        numericBuckets: numeric,
        specialBuckets: special,
        lowerBound: values.length ? Math.floor(Math.min(...values)) : 0,
        upperBound: values.length ? Math.ceil(Math.max(...values)) : 20,
      };
    }, [buckets]);

  const [localMin, setLocalMin] = useState(min || lowerBound);
  const [localMax, setLocalMax] = useState(max || upperBound);

  useEffect(() => {
    setLocalMin(min || lowerBound);
  }, [min, lowerBound]);
  useEffect(() => {
    setLocalMax(max || upperBound);
  }, [max, upperBound]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMinChange = useCallback(
    debounce((value: number) => onMinChange(value), 500),
    [onMinChange]
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMaxChange = useCallback(
    debounce((value: number) => onMaxChange(value), 500),
    [onMaxChange]
  );

  const handleLowChange = (e: { target: { value: string } }) => {
    const val = Number(e.target.value);
    const clamped = Math.min(val, localMax - 1);
    setLocalMin(clamped);
    debouncedMinChange(clamped <= lowerBound ? 0 : clamped);
  };

  const handleHighChange = (e: { target: { value: string } }) => {
    const val = Number(e.target.value);
    const clamped = Math.max(val, localMin + 1);
    setLocalMax(clamped);
    debouncedMaxChange(clamped >= upperBound ? 0 : clamped);
  };

  const range = upperBound - lowerBound || 1;
  const leftPercent = ((localMin - lowerBound) / range) * 100;
  const rightPercent = ((upperBound - localMax) / range) * 100;

  const hasRangeActive = min > 0 || max > 0;
  const hasAnyActive = hasRangeActive || selectedSpecial.length > 0;

  return (
    <div className="border-base-content/10 border-b last:border-b-0">
      <div className="hover:bg-base-300 flex items-center transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm font-medium"
          onClick={handleToggle}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 transition-transform ${isOpen ? "" : "-rotate-90"}`}
          />
          <span className="flex-1 text-left">{title}</span>
          {gameExclusive && (
            <span className="text-base-content/40 flex items-center">
              <GameIcon game={gameExclusive} />
            </span>
          )}
          {hasRangeActive && (
            <span className="badge badge-primary badge-xs">
              {min || lowerBound}–{max || upperBound}
            </span>
          )}
          {selectedSpecial.length > 0 && (
            <span className="badge badge-primary badge-xs">
              {selectedSpecial.length}
            </span>
          )}
        </button>
        {hasAnyActive && onClear && (
          <button
            type="button"
            className="text-base-content/50 hover:text-error pr-2 text-xs transition-colors"
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-2 px-3 pb-3 pt-1">
            {/* Numeric range slider */}
            {numericBuckets.length > 0 && (
              <div>
                <FacetDistribution
                  buckets={numericBuckets}
                  lowerBound={lowerBound}
                  upperBound={upperBound}
                  activeMin={localMin}
                  activeMax={localMax}
                />
                <div className="relative h-6">
                  <div className="bg-base-300 absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full" />
                  <div
                    className="bg-primary absolute top-1/2 h-1 -translate-y-1/2 rounded-full"
                    style={{
                      left: `${leftPercent}%`,
                      right: `${rightPercent}%`,
                    }}
                  />
                  <input
                    type="range"
                    min={lowerBound}
                    max={upperBound}
                    step={1}
                    value={localMin}
                    onChange={handleLowChange}
                    className="[&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:bg-primary pointer-events-none absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full"
                  />
                  <input
                    type="range"
                    min={lowerBound}
                    max={upperBound}
                    step={1}
                    value={localMax}
                    onChange={handleHighChange}
                    className="[&::-moz-range-thumb]:bg-primary [&::-webkit-slider-thumb]:bg-primary pointer-events-none absolute inset-0 z-20 m-0 h-full w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full"
                  />
                </div>
                <div className="text-base-content/50 flex justify-between text-xs">
                  <span>{localMin}</span>
                  <span>{localMax}</span>
                </div>
              </div>
            )}

            {/* Special value pills */}
            {specialBuckets.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {specialBuckets.map((bucket) => {
                  const val = bucket.value == null ? "" : String(bucket.value);
                  const label = formatFacetLabel(bucket.value);
                  const isSelected = selectedSpecial.includes(val);
                  const isDisabled = bucket.count === 0 && !isSelected;

                  return (
                    <button
                      key={val || "__none__"}
                      type="button"
                      className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                        isSelected
                          ? "border-primary/50 bg-primary/5 text-primary"
                          : "border-base-content/15 hover:border-base-content/30 text-base-content/70"
                      } ${isDisabled ? "pointer-events-none opacity-40" : ""}`}
                      onClick={() => onToggleSpecial(val)}
                    >
                      <span>{label}</span>
                      <span
                        className={`tabular-nums ${isSelected ? "text-primary/60" : "text-base-content/40"}`}
                      >
                        {bucket.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {!buckets?.length && (
              <span className="text-base-content/40 px-1 py-1 text-xs">
                No values available
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
